import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  DataTable,
  Page,
  Card,
  Text,
  ButtonGroup,
  Button,
  Modal,
  TextField,
  Spinner,
  Image,
  Toast,
} from "@shopify/polaris";
import axios from "../../../Assets/Lib/axios";
import emptyListImage from "../../../Assets/Images/emptyList.svg";
import MyPagination from "@material-ui/lab/Pagination";
import { useNavigate } from "react-router-dom";
import { CategoriesIcon, EditIcon, DeleteIcon } from "@shopify/polaris-icons"; // added DeleteIcon
import lebanonIcon from "../../../Assets/Images/lb.svg";
import iraqIcon from "../../../Assets/Images/iq.svg";
import egyptIcon from "../../../Assets/Images/eg.svg";
import { SaveMinor } from "@shopify/polaris-icons"; // for save icon

const TagsPersonas = () => {
  const navigate = useNavigate();

  // Filters state
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("All");
  const [country, setCountry] = useState("Lebanon");

  // Data state
  const [items, setItems] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isListEmpty, setIsListEmpty] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 50;
  const refBoolPage = useRef(true);

  // Toast
  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive(!active), []);
  const [toastContent, setToastContent] = useState("");

  // Modal for Add/Edit
  const [modalActive, setModalActive] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" or "edit"
  const [currentName, setCurrentName] = useState("");
  const [currentEditId, setCurrentEditId] = useState(null);

  // Modal for Delete
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [currentDeleteId, setCurrentDeleteId] = useState(null);

  const handleModalToggle = useCallback(
    () => setModalActive(!modalActive),
    [modalActive]
  );

  const handleDeleteModalToggle = useCallback(
    () => setDeleteModalActive(!deleteModalActive),
    [deleteModalActive]
  );

  const handleAddItem = () => {
    setModalMode("add");
    setCurrentName("");
    setModalActive(true);
  };

  const handleEdit = (id, name) => {
    setModalMode("edit");
    setCurrentName(name || "");
    setCurrentEditId(id);
    setModalActive(true);
  };

  const handleDelete = (id) => {
    setCurrentDeleteId(id);
    setDeleteModalActive(true);
  };

  const handleSaveName = async () => {
    setIsSaving(true);
    const form_data = new FormData();
    form_data.append("text", currentName);
    try {
      if (modalMode === "add") {
        await axios.post("/person-tags", form_data);
        setToastContent("Tag added successfully");
      } else {
        form_data.append("_method", "PATCH");
        await axios.post(`/person-tags/${currentEditId}`, form_data);
        setToastContent("Tag updated successfully");
      }
      setIsSaving(false);
      toggleActive();
      fetchData();
      handleModalToggle();
    } catch (err) {
      console.error(err);
    }
    setIsLoading(false);
  };

  const confirmDelete = async () => {
    setIsSaving(true);
    try {
      await axios.delete(`/person-tags/${currentDeleteId}`);
      setToastContent("Tag deleted successfully");
      toggleActive();
      fetchData();
    } catch (err) {
      console.error(err);
    }
    setDeleteModalActive(false);
    setIsSaving(false);
  };

  const handleRowClick = (id) => {
    //navigate(`/admin/Categories/${id}/metrics`);
  };

  const handleChangePage = (event, value) => {
    refBoolPage.current = true;
    setPage(value);
  };

  // Fetch data function
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("per_page", perPage);
      params.append("page", refBoolPage.current ? page : 1);
      if (search) params.append("filter[text]", search);

      const response = await axios.get(`/person-tags`, { params });
      const data = response.data.data.data;

      setItems(
        data.map((item) => [
          <div onClick={() => handleRowClick(item.id)} className="row_click">
            {item.text || "-"}
          </div>,

          <div className="me-3">
            <ButtonGroup>
              <Button
                onClick={() => handleEdit(item.id, item.text)}
                icon={EditIcon}
              >
                Edit
              </Button>
              <Button
                onClick={() => handleDelete(item.id)}
                icon={DeleteIcon}
                tone="critical"
              >
                Delete
              </Button>
            </ButtonGroup>
          </div>,
        ])
      );

      setTotalPages(response.data.data.total);
      setIsListEmpty(!data.length);
    } catch (err) {
      console.error(err);
      setIsListEmpty(true);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [page, search, channelFilter, country]);
  return items ? (
    <Page
      title="Tags"
      primaryAction={
        <Button variant="primary" onClick={handleAddItem}>
          Add Tag
        </Button>
      }
      backAction={{ onAction: () => navigate(`/admin/personas`) }}
    >
      {/* DataTable */}
      {items?.length ? (
        <Card padding="0">
          <DataTable
            columnContentTypes={["text", "text", "text", "text"]}
            headings={["Text", ""]}
            rows={items}
            verticalAlign="middle"
            stickyHeader
            footerContent={
              items?.length &&
              `Showing ${items.length} of ${totalPages} results`
            }
          />
        </Card>
      ) : (
        <Card>
          <div
            style={{
              display: "flex",
              justifyContent: "middle",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div style={{ width: "25%" }}>
              <img src={emptyListImage}></img>
            </div>
            <div className="mb-3">
              <Text variant="headingLg" as="h5" color="subdued">
                No results found
              </Text>
            </div>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {Math.ceil(totalPages / perPage) >= 2 && (
        <MyPagination
          count={Math.ceil(totalPages / perPage)}
          page={page}
          onChange={handleChangePage}
          size="large"
        />
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={modalActive}
        onClose={handleModalToggle}
        title={modalMode === "add" ? "Add Tag" : "Edit Tag"}
        primaryAction={{
          content: "Save",
          onAction: handleSaveName,
          loading: isSaving,
        }}
      >
        <Modal.Section>
          <TextField
            label="Tag Name"
            value={currentName}
            onChange={setCurrentName}
            autoComplete="off"
            placeholder="Enter name"
          />
        </Modal.Section>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={deleteModalActive}
        onClose={handleDeleteModalToggle}
        title="Delete Tag"
        primaryAction={{
          content: "Yes",
          destructive: true,
          onAction: confirmDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          {
            content: "No",
            loading: isSaving,
            onAction: handleDeleteModalToggle,
          },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this tag?</Text>
        </Modal.Section>
      </Modal>

      {active && <Toast content={toastContent} onDismiss={toggleActive} />}
    </Page>
  ) : (
    <div className="spinnerContainer">
      <div className="vertical-center">
        <Spinner size="large" />
      </div>
    </div>
  );
};

export default TagsPersonas;
